import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { STATES } from '../../utils/appState';
import './AppLifecycle.css';

const LADDER = [STATES.designing, STATES.registered, STATES.testing, STATES.live, STATES.charging];

/**
 * Where an app stands on the five states it moves through — the same ladder
 * Home's pipeline draws across the whole fleet, here for one app: earlier
 * states checked, the current one lit, the rest still ahead.
 *
 *   Designing  → no design document (CRUX) yet
 *   Registered → designed; nobody known to have authorized it
 *   Testing    → authorized, possibly only by its own test users
 *   Live       → at least one real person authorized it
 *   Charging   → live, priced, and payouts are ready
 *
 * The rungs come from utils/appState.js deriveState, which never ticks a rung
 * on facts it does not have (no "Designing ✓" while Next says there is no
 * design document; no "Charging" while payouts cannot be paid into).
 */
export function LifecycleStrip({ state }) {
  const now = state?.rank ?? 0;
  return (
    <ol className="lifecycle" aria-label="Where this app stands">
      {LADDER.map((s) => {
        const at = s.rank < now ? 'done' : s.rank === now ? 'now' : 'ahead';
        return (
          <li key={s.key} className="lifecycle__step" data-at={at} aria-current={at === 'now' ? 'step' : undefined}>
            <span className="lifecycle__n" aria-hidden="true">
              {at === 'done' ? <Check size={10} strokeWidth={3} /> : s.rank + 1}
            </span>
            <span className="lifecycle__label">{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The one thing worth doing about this app (utils/appState.js deriveBlocking),
 * with the press that gets you to it — or a quiet line when nothing waits.
 * A staged draft is not shown here: the DraftBar stands in this place then,
 * because committing is the next thing and it owns that flow.
 *
 * `checking`: the facts are still loading, so "Nothing waiting." would be a
 * claim made from nothing — it says it is still checking instead.
 */
export function NextBar({ blocking, onAct, checking = false }) {
  const quiet = checking && !blocking ? 'Checking what it needs…' : 'Nothing waiting.';
  return (
    <div className="nextbar" role="status" data-quiet={blocking ? undefined : true}>
      <span className="nextbar__k">Next</span>
      <span className="nextbar__t">{blocking ? blocking.text : quiet}</span>
      {blocking && (
        <button type="button" className="nextbar__go" onClick={() => onAct(blocking)}>
          {blocking.action} <ArrowRight size={13} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
